import Logo from '@assets/images/common/brand/logo-primary.svg';
import SvgIconArticle from '@assets/images/common/icon/category/SvgIconArticle';
import ChatBalloon from '@assets/images/common/icon/channel/chat_balloon.svg';
import ChatLock from '@assets/images/common/icon/channel/lock.svg';
import LockIcon from '@assets/images/common/icon/lock/lock.svg';
import { DxDiagnosisMenu } from '@src/api/types/dxDiagnosisMenu';
import { brandColors } from '@src/assets/colors/brandColors';
import SvgIconEvent from '@src/assets/images/common/icon/category/SvgIconEvent';
import SvgIconService from '@src/assets/images/common/icon/category/SvgIconService';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { COMPANY_URL } from '@src/routes/company';
import { Link } from 'react-router-dom';
import { EventMenu } from 'src/api/types/eventMenu';

type Category = {
  label: string;
  to: string;
  isCurrent: boolean;
};
type Props = {
  categories: Category[];
  eventMenu: EventMenu[];
  dxDiagnosisMenu: DxDiagnosisMenu[];
  top_page_link: string;
  pageName?: string;
  settingMenuItems: SettingMenuItem[];
};

type SettingMenuItem = {
  category: string;
  items: {
    title: string;
    link: string;
    adminOnly: boolean;
    isComingSoon: boolean;
    isActive: boolean;
  }[];
};

const dxDiagnosisMenuStyle = {
  member: 'items-center py-3 pl-4 xl:px-1',
  notLogin: 'items-center py-[11px] pl-[19px] xl:px-[7px]',
};

const renderLockIcon = () => (
  <img
    src={LockIcon}
    alt=''
    width='24'
    height='24'
  />
);

const CompanySidebar = ({
  categories,
  eventMenu,
  dxDiagnosisMenu,
  top_page_link,
  pageName,
  settingMenuItems,
}: Props) => {
  const companyAuth = useCompanyAuth();

  const isAdmin = companyAuth.profile?.isLogin && companyAuth.profile.user?.isAdmin;

  return (
    <aside className='size-full border-r border-solid border-r-gray-200 bg-white'>
      <Link
        to={top_page_link}
        className='block p-[10px]'
      >
        <img
          src={Logo}
          alt=''
          className='block size-full max-h-[40px] object-contain'
        />
      </Link>
      {/* 組織管理と個人管理のページのみ表示 */}
      {(pageName === 'users' || pageName === 'companies' || pageName === 'profile') && (
        <div className='flex flex-col gap-4 px-4 text-sm'>
          {settingMenuItems.map((section, index) => (
            <div key={index}>
              <p className='py-3'>{section.category}</p>
              <ul>
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <Link
                      to={item.link}
                      className={`flex items-start gap-2 py-3 pl-7 pr-5 before:size-1 before:flex-[0_0_auto] before:translate-y-[9px] before:rounded-full before:bg-black xl:px-4 ${
                        item.isActive ? 'bg-primary-50' : ''
                      }`}
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {/* メディア */}
      {pageName === 'articles' && (
        <ul className='px-4 pb-8'>
          {categories.map((category) => (
            <li key={category.to}>
              <Link
                className='flex w-full'
                to={category.to}
              >
                <div
                  className={`flex items-center gap-2 truncate py-3 ${
                    category.isCurrent && '!border-b-2 border-b-primary-500 text-primary-500'
                  }`}
                >
                  <SvgIconArticle
                    color={category.isCurrent ? brandColors['500'] : '#d9d9d9'}
                    style={{
                      width: '14px',
                      height: '14px',
                      paddingTop: '2px',
                      flex: '0 0 auto',
                    }}
                  />
                  <span className='truncate text-sm'>{category.label}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {/* 支援サービス */}
      {pageName === 'support-services' && (
        <ul className='px-4 pb-8'>
          {categories.map((category) => (
            <li key={category.to}>
              <Link
                className='flex w-full'
                to={category.to}
              >
                <div
                  className={`flex items-center gap-2 truncate py-3 ${
                    category.isCurrent && '!border-b-2 border-b-primary-500 text-primary-500'
                  }`}
                >
                  <SvgIconService
                    color={category.isCurrent ? brandColors['500'] : '#d9d9d9'}
                    style={{
                      width: '14px',
                      height: '14px',
                      paddingTop: '2px',
                      flex: '0 0 auto',
                    }}
                  />
                  <span className='truncate text-sm'>{category.label}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {/* DX診断 */}
      {pageName === 'dx-diagnosis' && (
        <div className='flex flex-col gap-4 px-4 text-sm'>
          {dxDiagnosisMenu.map((section, index) => (
            <div key={index}>
              <p className='py-3'>{section.title}</p>
              <ul>
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    {isAdmin || item.link === COMPANY_URL.DX_DIAGNOSIS_INTRODUCTION ? (
                      <Link
                        to={item.link}
                        className={`flex items-start gap-2 ${
                          item.isActive ? 'bg-primary-50' : ''
                        } py-3 pl-7 pr-5 before:size-1 before:flex-[0_0_auto] before:translate-y-[9px] before:rounded-full before:bg-black xl:px-4`}
                      >
                        {item.title}
                      </Link>
                    ) : (
                      <span
                        className={`flex items-start pr-5 ${
                          companyAuth.profile?.isLogin
                            ? dxDiagnosisMenuStyle.member
                            : dxDiagnosisMenuStyle.notLogin
                        } ${item.isActive ? 'bg-primary-50' : ''}`}
                      >
                        {companyAuth.profile?.isLogin ? (
                          <span className='mr-[4px] translate-y-px border border-solid border-current px-1 text-[10px] text-black'>
                            管理者
                          </span>
                        ) : (
                          renderLockIcon()
                        )}
                        {item.title}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {section.message && (
                <p className='mt-4 rounded-md border border-solid border-gray-200 px-4 py-3 text-xs'>
                  {section.message}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {/* イベント一覧・詳細 */}
      {pageName === 'events' && (
        <ul className='px-4 pb-8'>
          {eventMenu.map((menu) => (
            <li key={menu.link}>
              <Link
                className='flex w-full'
                to={menu.link}
              >
                <div
                  className={`flex items-center gap-2 truncate py-3 ${
                    menu.isCurrent && '!border-b-2 border-b-primary-500 text-primary-500'
                  }`}
                >
                  <SvgIconEvent
                    color={menu.isCurrent ? brandColors['500'] : '#d9d9d9'}
                    style={{
                      width: '14px',
                      height: '14px',
                      paddingTop: '2px',
                      flex: '0 0 auto',
                    }}
                  />
                  <span className='truncate text-sm'>{menu.title}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {/* オンラインコミュニティ */}
      {pageName === 'community' && (
        <div className='flex flex-col gap-4 px-3 py-[8px] text-sm'>
          <div>
            <p className='py-3 font-semibold'>チャンネル</p>
            <ul className='flex flex-col gap-2 px-2 pt-2'>
              <li>
                <Link
                  to=''
                  className='flex w-full'
                >
                  <div className='flex items-center gap-2 truncate border-b-2 border-primary-500 pb-2'>
                    <img
                      src={ChatBalloon}
                      className='size-4'
                      alt=''
                    />
                    <div className={`w-fit truncate text-sm font-medium text-primary-500`}>
                      チャンネル名
                    </div>
                  </div>
                </Link>
              </li>
              <li>
                <Link
                  to=''
                  className='flex w-full'
                >
                  <div className='flex items-center gap-2 truncate pb-2'>
                    <img
                      src={ChatBalloon}
                      className='size-4'
                      alt=''
                    />
                    <div className={`w-fit truncate text-sm`}>チャンネル名</div>
                  </div>
                </Link>
              </li>
              <li>
                <Link
                  to=''
                  className='flex w-full'
                >
                  <div className='flex items-center gap-2 truncate pb-2'>
                    <div className='size-4 text-sm leading-none'>🧐</div>
                    <div className={`w-fit truncate text-sm`}>チャンネル名</div>
                  </div>
                </Link>
              </li>
              <li>
                <div className='flex w-full'>
                  <div className='flex w-full items-center gap-2 truncate pb-2'>
                    <img
                      src={ChatBalloon}
                      className='size-4'
                      alt=''
                    />
                    <div className={`w-full truncate text-sm`}>チャンネル名チャンネル名</div>
                  </div>
                  <img
                    className='size-5 flex-[0_0_auto]'
                    width='20'
                    height='20'
                    src={ChatLock}
                    alt=''
                  />
                </div>
              </li>
              <li>
                <div className='flex w-full'>
                  <div className='flex w-full items-center gap-2 truncate pb-2'>
                    <div className='size-4 flex-[0_0_auto] text-sm leading-none'>🧐</div>
                    <div className={`w-full truncate text-sm`}>チャンネル名チャンネル名</div>
                  </div>
                  <img
                    className='size-5 flex-[0_0_auto]'
                    width='20'
                    height='20'
                    src={ChatLock}
                    alt=''
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      )}
    </aside>
  );
};

export default CompanySidebar;
